import React from "react";
import { colors } from "src/styles";
import { Asset } from "src/types";
import { ASSETS_TABLE_HEADERS } from "../constants";
import styled from "styled-components";

export interface AssetsTableProps {
  data: Asset[];
  onRowClick: (asset: Asset) => void;
}

export const AssetsTable = ({ data, onRowClick }: AssetsTableProps) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.getFullYear().toString();
    } catch {
      return dateString;
    }
  };

  if (!data.length) {
    return <EmptyRow>No assets found.</EmptyRow>;
  }

  return (
    <TableWrap>
      <TableHeader>
        {ASSETS_TABLE_HEADERS.map((header) => (
          <span key={header}>{header}</span>
        ))}
      </TableHeader>
      {data.map((asset: Asset) => {
        const assetCode = asset.assetnum || asset.assetCode || "N/A";
        const assetStatus = asset.status_description || asset.status || "N/A";

        return (
          <TableRow key={asset.id} onClick={() => onRowClick(asset)}>
            <MutedCell>{asset.location_description || <ItalicDash>N/A</ItalicDash>}</MutedCell>
            <IdPill>{assetCode}</IdPill>
            <NameCell>{asset.description || "N/A"}</NameCell>
            <MutedCell>{asset.assettype_description || asset.assettype || asset.category || "N/A"}</MutedCell>
            <MutedCell>{asset.location || <ItalicDash>N/A</ItalicDash>}</MutedCell>
            <RightSmall>{formatDate(asset.installdate) || asset.installYear || "N/A"}</RightSmall>
            <StatusCell>{assetStatus}</StatusCell>
          </TableRow>
        );
      })}
    </TableWrap>
  );
};

const EmptyRow = styled.div`
  padding: 20px 16px;
  color: ${colors.grey.dark6};
  font-size: 13px;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const TableGridBase = styled.div`
  display: grid;
  grid-template-columns: minmax(190px, 1fr) 140px minmax(240px, 1.3fr) minmax(180px, 1fr) minmax(190px, 1fr) 110px 120px;
  gap: 8px;
  align-items: center;
  min-width: 1180px;
`;

const TableHeader = styled(TableGridBase)`
  padding: 12px 16px;
  color: ${colors.grey.dark6};
  border-bottom: 1px solid ${colors.grey.light3};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
`;

const TableRow = styled(TableGridBase)`
  padding: 14px 16px;
  border-top: 1px solid ${colors.grey.light3};
  cursor: pointer;

  &:hover {
    background: ${colors.grey.light6};
  }
`;

const IdPill = styled.span`
  border: 1px solid ${colors.grey.light4};
  background: ${colors.grey.light6};
  font-size: 11px;
  font-weight: 700;
  color: ${colors.black.light1};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 8px;
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NameCell = styled.span`
  font-size: 13px;
  color: ${colors.black.dark1};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MutedCell = styled.span`
  font-size: 12px;
  color: ${colors.grey.dark6};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RightSmall = styled.span`
  text-align: right;
  font-size: 11px;
  color: ${colors.grey.dark6};
`;

const StatusCell = styled.span`
  font-size: 12px;
  color: ${colors.black.dark1};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItalicDash = styled.span`
  color: ${colors.grey.dark7};
  font-style: italic;
`;
